import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';
import RichTextEditor from './RichTextEditor';

const EnhancedProductForm = ({ 
  formData, 
  setFormData, 
  categories, 
  materials, 
  flatRates,
  handleSubmit, 
  handleImageUpload,
  handleRemoveImage,
  handleAddonImageUpload,
  editingProduct,
  uploadingImages,
  uploadingAddonImages,
  getImageUrl,
  formMode = 'tabbed', // 'wizard', 'tabbed', or 'fullpage'
  products = [] // List of all products for linking as addons
}) => {
  const theme = useThemeClasses();
  const [isWizardMode, setIsWizardMode] = useState(formMode === 'wizard');
  const isFullPageMode = formMode === 'fullpage';
  const [currentStep, setCurrentStep] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [completedTabs, setCompletedTabs] = useState(new Set());

  const tabs = [
    { 
      id: 'basic', 
      label: 'Basic Info', 
      icon: '📝',
      description: 'Essential product details',
      required: true
    },
    { 
      id: 'images', 
      label: 'Images & Media', 
      icon: '🖼️',
      description: 'Product photos and gallery',
      required: false
    },
    { 
      id: 'inventory', 
      label: 'Inventory', 
      icon: '📦',
      description: 'Stock tracking and availability',
      required: false
    },
    { 
      id: 'costing', 
      label: 'Costing Model', 
      icon: '💰',
      description: 'Materials and cost breakdown',
      required: false
    },
    { 
      id: 'customization', 
      label: 'Customization', 
      icon: '⚙️',
      description: 'Options, variants, and add-ons',
      required: false
    },
    { 
      id: 'files', 
      label: 'Customer Files', 
      icon: '📎',
      description: 'Upload settings for customers',
      required: false
    },
    { 
      id: 'shipping', 
      label: 'Shipping', 
      icon: '🚚',
      description: 'Dimensions and shipping details',
      required: false
    }
  ];

  // Track unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        document.getElementById('product-form-submit')?.click();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Check tab completion
  const checkTabCompletion = (tabId) => {
    switch (tabId) {
      case 'basic':
        return formData.name && formData.price && formData.category && formData.description;
      case 'images':
        return formData.images && formData.images.length > 0;
      case 'inventory':
        return true; // Optional
      case 'costing':
        return true; // Optional
      case 'customization':
        return true; // Optional
      case 'files':
        return true; // Optional
      case 'shipping':
        return formData.weight > 0;
      default:
        return false;
    }
  };

  // Update completion status
  useEffect(() => {
    const newCompleted = new Set();
    tabs.forEach(tab => {
      if (checkTabCompletion(tab.id)) {
        newCompleted.add(tab.id);
      }
    });
    setCompletedTabs(newCompleted);
  }, [formData]);

  const handleNext = () => {
    if (currentStep < tabs.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTabChange = (index) => {
    if (!isWizardMode) {
      setCurrentStep(index);
    }
  };

  const toggleWizardMode = () => {
    setIsWizardMode(!isWizardMode);
    toast.success(isWizardMode ? 'Switched to Tab Mode' : 'Switched to Wizard Mode');
  };

  const renderProgressBar = () => {
    if (!isWizardMode) return null;
    
    const progress = ((currentStep + 1) / tabs.length) * 100;
    
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className={`text-sm font-medium ${theme.textPrimary}`}>
            Step {currentStep + 1} of {tabs.length}
          </span>
          <span className={`text-sm ${theme.textSecondary}`}>
            {Math.round(progress)}% Complete
          </span>
        </div>
        <div className={`w-full h-2 ${theme.bgSecondary} rounded-full overflow-hidden`}>
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  const renderTabNavigation = () => {
    return (
      <div className="mb-6">
        <div className={`border-b ${theme.border}`}>
          <nav className="flex space-x-1 overflow-x-auto pb-px">
            {tabs.map((tab, index) => {
              const isActive = currentStep === index;
              const isCompleted = completedTabs.has(tab.id);
              const isRequired = tab.required;
              
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(index)}
                  disabled={isWizardMode && index > currentStep}
                  className={`
                    flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                    ${isActive 
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
                      : `border-transparent ${theme.textSecondary} ${isWizardMode && index > currentStep ? 'opacity-50 cursor-not-allowed' : 'hover:text-blue-600 dark:hover:text-blue-400 hover:border-gray-300'}`
                    }
                  `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                  {isRequired && !isCompleted && (
                    <span className="text-red-500 text-xs">*</span>
                  )}
                  {isCompleted && (
                    <span className="text-green-500 text-xs">✓</span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
        
        {/* Tab Description */}
        <div className={`mt-3 p-3 ${theme.bgSecondary} rounded-lg`}>
          <p className={`text-sm ${theme.textSecondary}`}>
            {tabs[currentStep].description}
            {tabs[currentStep].required && (
              <span className="ml-2 text-red-500 text-xs font-medium">* Required</span>
            )}
          </p>
        </div>
      </div>
    );
  };

  const renderBasicInfo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
            Product Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData({...formData, name: e.target.value});
              setHasUnsavedChanges(true);
            }}
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
            placeholder="e.g., Custom 3D Printed Phone Case"
            required
          />
          {!formData.name && (
            <p className="text-xs text-red-500 mt-1">Product name is required</p>
          )}
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
            Price ($) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.price}
            onChange={(e) => {
              setFormData({...formData, price: e.target.value});
              setHasUnsavedChanges(true);
            }}
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
            placeholder="0.00"
            required
          />
          {formData.price && parseFloat(formData.price) > 0 && (
            <p className="text-xs text-green-600 mt-1">✓ Valid price</p>
          )}
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
            Category <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.category}
            onChange={(e) => {
              setFormData({...formData, category: e.target.value});
              setHasUnsavedChanges(true);
            }}
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
            required
          >
            <option value="">Select a category</option>
            {categories.map((category) => {
              const isSubCategory = !!category.parent_id;
              const parentCategory = categories.find(c => c.id === category.parent_id);
              const displayName = isSubCategory && parentCategory 
                ? `${parentCategory.name} → ${category.name}` 
                : category.name;
              
              return (
                <option key={category.id} value={category.name}>
                  {isSubCategory ? '├─ ' : ''}{displayName}
                </option>
              );
            })}
          </select>
          {categories.length === 0 && (
            <p className={`text-xs ${theme.textSecondary} mt-1`}>
              ⚠️ No categories available. Create categories first.
            </p>
          )}
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
            Featured Product
          </label>
          <div className="flex items-center h-10">
            <input
              type="checkbox"
              id="featured"
              checked={formData.featured}
              onChange={(e) => {
                setFormData({...formData, featured: e.target.checked});
                setHasUnsavedChanges(true);
              }}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="featured" className={`ml-2 text-sm ${theme.textSecondary}`}>
              Display on homepage
            </label>
          </div>
        </div>
      </div>

      <div>
        <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
          Description <span className="text-red-500">*</span>
        </label>
        <p className={`text-xs ${theme.textSecondary} mb-2`}>
          Use the rich text editor to format your product description with headings, bold, italic, lists, and more.
        </p>
        <RichTextEditor
          value={formData.description}
          onChange={(content) => {
            setFormData({...formData, description: content});
            setHasUnsavedChanges(true);
          }}
          placeholder="Write a detailed and well-formatted product description..."
        />
        <div className="flex justify-between items-center mt-2">
          <p className={`text-xs ${theme.textSecondary}`}>
            {formData.description ? `${formData.description.replace(/<[^>]*>/g, '').length} characters` : '0 characters'}
          </p>
          {formData.description && formData.description.replace(/<[^>]*>/g, '').length > 100 && (
            <p className="text-xs text-green-600">✓ Good description length</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderImages = () => (
    <div className="space-y-4">
      {/* Drag & Drop Upload */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
          Upload Product Images
        </label>
        
        <label className="block">
          <div 
            className={`cursor-pointer px-6 py-8 border-2 border-dashed ${theme.border} rounded-lg text-center hover:border-blue-500 transition-colors ${uploadingImages ? 'opacity-50 cursor-not-allowed' : ''} ${theme.bgSecondary}`}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
              if (!uploadingImages) {
                handleImageUpload(e.dataTransfer.files);
                setHasUnsavedChanges(true);
              }
            }}
          >
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => {
                handleImageUpload(e.target.files);
                setHasUnsavedChanges(true);
                // Reset input so the same files can be selected again if needed
                e.target.value = '';
              }}
              className="hidden"
              disabled={uploadingImages}
              id="product-image-upload"
            />
            <div className="flex flex-col items-center gap-3">
              {uploadingImages ? (
                <>
                  <svg className="animate-spin w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm font-medium text-blue-500">Uploading images...</p>
                </>
              ) : (
                <>
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div className="text-center">
                    <span className={`text-base ${theme.textSecondary}`}>
                      <span className="text-blue-600 dark:text-blue-400 font-semibold">Click to upload</span> or drag and drop
                    </span>
                    <p className={`text-xs ${theme.textSecondary} mt-1`}>PNG, JPG, WebP (Max 5MB per image)</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
                    <p className={`text-sm font-medium text-blue-700 dark:text-blue-300`}>
                      💡 Select multiple images at once (Ctrl/Cmd + Click)
                    </p>
                    <p className={`text-xs text-blue-600 dark:text-blue-400 mt-1`}>
                      All images will upload automatically - no need to save between uploads
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </label>
      </div>

      {/* Existing Images Grid */}
      {formData.images && formData.images.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className={`block text-sm font-medium ${theme.textPrimary}`}>
              Current Images ({formData.images.length})
            </label>
            <button
              type="button"
              onClick={() => document.getElementById('product-image-upload').click()}
              disabled={uploadingImages}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploadingImages ? 'Uploading...' : '+ Add More Images'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {formData.images.map((image, index) => (
              <div key={index} className={`relative group rounded-lg overflow-hidden border-2 ${index === 0 ? 'border-blue-500' : theme.border} ${theme.bgSecondary}`}>
                <img
                  src={getImageUrl ? getImageUrl(image) : (typeof image === 'string' ? image : URL.createObjectURL(image))}
                  alt={`Product ${index + 1}`}
                  className="w-full h-40 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      handleRemoveImage(index);
                      setHasUnsavedChanges(true);
                    }}
                    className="opacity-0 group-hover:opacity-100 bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 transition-all text-sm"
                  >
                    Remove
                  </button>
                </div>
                {index === 0 && (
                  <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    Main Image
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(!formData.images || formData.images.length === 0) && (
        <div className={`p-6 ${theme.bgSecondary} border ${theme.border} rounded-lg text-center`}>
          <p className={`text-sm ${theme.textSecondary}`}>
            📸 No images uploaded yet. Add at least one image to showcase your product.
          </p>
        </div>
      )}
    </div>
  );

  const renderInventory = () => (
    <div className="space-y-4">
      <div className={`${theme.bgSecondary} p-5 rounded-lg border ${theme.border}`}>
        <div className="space-y-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="track_inventory"
              checked={formData.track_inventory}
              onChange={(e) => {
                setFormData({...formData, track_inventory: e.target.checked});
                setHasUnsavedChanges(true);
              }}
              className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="track_inventory" className="ml-3">
              <div className={`text-sm font-medium ${theme.textPrimary}`}>Enable Stock Tracking</div>
              <div className={`text-xs ${theme.textSecondary}`}>Track inventory levels and get low stock alerts</div>
            </label>
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="is_made_to_order"
              checked={formData.is_made_to_order}
              onChange={(e) => {
                setFormData({...formData, is_made_to_order: e.target.checked});
                setHasUnsavedChanges(true);
              }}
              className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_made_to_order" className="ml-3">
              <div className={`text-sm font-medium ${theme.textPrimary}`}>Made to Order (Unlimited Supply)</div>
              <div className={`text-xs ${theme.textSecondary}`}>This product is made on demand - no stock limitations</div>
            </label>
          </div>

          {formData.track_inventory && !formData.is_made_to_order && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pl-7">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                  Current Stock Quantity
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.stock_quantity}
                  onChange={(e) => {
                    setFormData({...formData, stock_quantity: parseInt(e.target.value) || 0});
                    setHasUnsavedChanges(true);
                  }}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                  Low Stock Alert Threshold
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.low_stock_threshold}
                  onChange={(e) => {
                    setFormData({...formData, low_stock_threshold: parseInt(e.target.value) || 5});
                    setHasUnsavedChanges(true);
                  }}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                />
                <p className={`text-xs mt-1 ${theme.textSecondary}`}>Alert when stock falls below this level</p>
              </div>
            </div>
          )}

          {formData.is_made_to_order && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`text-sm ${theme.textPrimary}`}>
                  ✨ This product will show as always in stock with unlimited supply
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderCostingModel = () => {
    // Calculate totals
    const materialCost = formData.cost_breakdown?.materials?.reduce((sum, m) => sum + (m.total_cost || 0), 0) || 0;
    const laborCost = formData.cost_breakdown?.labor_cost || 0;
    const overheadCost = formData.cost_breakdown?.overhead_cost || 0;
    const totalCost = materialCost + laborCost + overheadCost;
    
    // Calculate actual profit margin based on product price and total costs
    const productPrice = parseFloat(formData.price) || 0;
    const profitMargin = productPrice > 0 && totalCost > 0 
      ? ((productPrice - totalCost) / productPrice * 100) 
      : 0;
    const profitAmount = productPrice - totalCost;

    // Weight conversion factors (to grams)
    const WEIGHT_CONVERSIONS = {
      'g': 1,
      'kg': 1000,
      'lb': 453.592,
      'oz': 28.3495
    };

    const addMaterial = () => {
      const newMaterial = {
        material_id: '',
        quantity: 0,
        unit: 'pieces',
        cost_per_unit: 0,
        total_cost: 0,
        is_weight_based: false,
        weight_used: 0,
        weight_unit: 'g'
      };
      setFormData({
        ...formData,
        cost_breakdown: {
          ...formData.cost_breakdown,
          materials: [...(formData.cost_breakdown?.materials || []), newMaterial]
        }
      });
      setHasUnsavedChanges(true);
    };

    const updateMaterial = (index, updates, value) => {
      const updatedMaterials = [...(formData.cost_breakdown?.materials || [])];
      
      // Handle both single field update (field, value) and bulk updates (object)
      if (typeof updates === 'string') {
        // Old API: updateMaterial(index, 'field', value)
        const field = updates;
        updatedMaterials[index] = { ...updatedMaterials[index], [field]: value };
      } else {
        // New API: updateMaterial(index, { field1: value1, field2: value2 })
        updatedMaterials[index] = { ...updatedMaterials[index], ...updates };
      }
      
      const currentMaterial = updatedMaterials[index];
      const selectedMaterialData = materials?.find(m => m.id === currentMaterial.material_id);
      
      // Calculate total cost for this material
      if (currentMaterial.is_weight_based && selectedMaterialData) {
        // Weight-based calculation using material's weight and cost_per_unit
        const weightUsed = parseFloat(currentMaterial.weight_used) || 0;
        const weightUnit = currentMaterial.weight_unit || 'g';
        
        // Get material's weight per unit - use material_package_weight if material.weight not available
        let materialWeight = parseFloat(selectedMaterialData.weight) || 0;
        let materialWeightUnit = selectedMaterialData.weight_unit || 'g';
        
        // If material doesn't have weight, use the manual package weight entered by user
        if (materialWeight === 0 && currentMaterial.material_package_weight) {
          materialWeight = parseFloat(currentMaterial.material_package_weight) || 0;
          materialWeightUnit = currentMaterial.material_package_unit || 'g';
        }
        
        const materialCostPerUnit = parseFloat(selectedMaterialData.cost_per_unit) || 0;
        
        console.log('Weight-based calculation:', {
          weightUsed,
          weightUnit,
          materialWeight,
          materialWeightUnit,
          materialCostPerUnit,
          usingPackageWeight: !selectedMaterialData.weight && currentMaterial.material_package_weight
        });
        
        if (materialWeight > 0 && WEIGHT_CONVERSIONS[weightUnit] && WEIGHT_CONVERSIONS[materialWeightUnit]) {
          // Convert both weights to grams for calculation
          const weightUsedInGrams = weightUsed * WEIGHT_CONVERSIONS[weightUnit];
          const materialWeightInGrams = materialWeight * WEIGHT_CONVERSIONS[materialWeightUnit];
          
          // Calculate cost per gram from material's live cost calculation
          // Formula: cost_per_unit / weight_in_grams
          const costPerGram = materialCostPerUnit / materialWeightInGrams;
          
          // Calculate total cost: weight_used × cost_per_gram
          const totalCost = weightUsedInGrams * costPerGram;
          
          console.log('Calculated:', {
            weightUsedInGrams,
            materialWeightInGrams,
            costPerGram,
            totalCost
          });
          
          updatedMaterials[index].total_cost = totalCost;
          updatedMaterials[index].calculated_cost_per_gram = costPerGram;
        } else {
          console.error('Invalid weight data:', {
            materialWeight,
            weightUnit,
            materialWeightUnit,
            materialCostPerUnit,
            hasWeightConversion: !!WEIGHT_CONVERSIONS[weightUnit],
            hasMaterialWeightConversion: !!WEIGHT_CONVERSIONS[materialWeightUnit],
            needsPackageWeight: materialWeight === 0
          });
          updatedMaterials[index].total_cost = 0;
          updatedMaterials[index].calculated_cost_per_gram = 0;
        }
      } else {
        // Piece-based calculation
        const qty = parseFloat(currentMaterial.quantity) || 0;
        const cost = parseFloat(currentMaterial.cost_per_unit) || 0;
        updatedMaterials[index].total_cost = qty * cost;
      }

      setFormData({
        ...formData,
        cost_breakdown: {
          ...formData.cost_breakdown,
          materials: updatedMaterials,
          total_material_cost: updatedMaterials.reduce((sum, m) => sum + (m.total_cost || 0), 0)
        }
      });
      setHasUnsavedChanges(true);
    };

    const removeMaterial = (index) => {
      const updatedMaterials = formData.cost_breakdown?.materials?.filter((_, i) => i !== index) || [];
      setFormData({
        ...formData,
        cost_breakdown: {
          ...formData.cost_breakdown,
          materials: updatedMaterials,
          total_material_cost: updatedMaterials.reduce((sum, m) => sum + (m.total_cost || 0), 0)
        }
      });
      setHasUnsavedChanges(true);
    };

    return (
      <div className="space-y-4">
        <div className={`${theme.bgSecondary} p-5 rounded-lg border ${theme.border}`}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <span className="text-green-600 dark:text-green-400 font-bold">💰</span>
            </div>
            <div>
              <h4 className={`text-base font-semibold ${theme.textPrimary}`}>Cost Breakdown & Pricing</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Calculate production costs and optimal pricing</p>
            </div>
          </div>

          {/* Materials Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h5 className={`text-sm font-semibold ${theme.textPrimary}`}>Materials</h5>
              <button
                type="button"
                onClick={addMaterial}
                className="text-sm bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors"
                disabled={!materials || materials.length === 0}
                title={!materials || materials.length === 0 ? 'Loading materials...' : 'Add material'}
              >
                + Add Material
              </button>
            </div>

            {!materials || materials.length === 0 ? (
              <div className={`p-4 ${theme.bgSecondary} border ${theme.border} rounded-lg text-center`}>
                <p className={`text-sm ${theme.textSecondary}`}>
                  Loading materials database... Please wait.
                </p>
              </div>
            ) : formData.cost_breakdown?.materials && formData.cost_breakdown.materials.length > 0 ? (
              <div className="space-y-3">
                {formData.cost_breakdown.materials.map((material, index) => (
                  <div key={index} className={`${theme.card} p-4 rounded-lg border ${theme.border}`}>
                    {(() => {
                      const selectedMaterialData = materials?.find(m => m.id === material.material_id);
                      // Check if user has manually set calculation mode, otherwise auto-detect
                      const weightUnits = ['g', 'kg', 'lb', 'oz'];
                      const autoDetectWeightBased = selectedMaterialData && weightUnits.includes(selectedMaterialData.unit_of_measure);
                      // Use manual setting if available, otherwise use auto-detection
                      const isWeightBased = material.is_weight_based !== undefined ? material.is_weight_based : autoDetectWeightBased;
                      
                      // Debug logging
                      console.log('Costing Debug:', {
                        materialId: material.material_id,
                        foundData: !!selectedMaterialData,
                        unit_of_measure: selectedMaterialData?.unit_of_measure,
                        isWeightBased: isWeightBased,
                        material: material,
                        materialData: selectedMaterialData,
                        weightUnits: weightUnits
                      });
                      
                      return (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div className="md:col-span-2">
                              <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Material</label>
                              <select
                                value={material.material_id}
                                onChange={(e) => {
                                  const selectedMaterial = materials?.find(m => m.id === e.target.value);
                                  console.log('Material Selected:', selectedMaterial);
                                  
                                  if (selectedMaterial) {
                                    const weightUnits = ['g', 'kg', 'lb', 'oz'];
                                    const hasWeight = weightUnits.includes(selectedMaterial.unit_of_measure);
                                    console.log('Is Weight Based?', hasWeight, 'Unit:', selectedMaterial.unit_of_measure);
                                    
                                    // Update all fields in one call to avoid race conditions
                                    const updates = {
                                      material_id: e.target.value,
                                      is_weight_based: hasWeight,
                                      cost_per_unit: selectedMaterial.cost_per_unit || 0,
                                      unit: selectedMaterial.unit_of_measure || 'pieces'
                                    };
                                    
                                    if (hasWeight) {
                                      updates.weight_unit = selectedMaterial.unit_of_measure || 'g';
                                      updates.weight_used = 0;
                                    } else {
                                      updates.quantity = 0;
                                    }
                                    
                                    updateMaterial(index, updates);
                                  } else {
                                    updateMaterial(index, 'material_id', e.target.value);
                                  }
                                }}
                                className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                              >
                                <option value="">Select material</option>
                                {materials && materials.map(m => {
                                  const weightUnits = ['g', 'kg', 'lb', 'oz'];
                                  const isWeight = weightUnits.includes(m.unit_of_measure);
                                  return (
                                    <option key={m.id} value={m.id}>
                                      {m.name} (${(m.cost_per_unit || 0).toFixed(2)}/{m.unit_of_measure || 'unit'})
                                      {isWeight && ' - Weight-based'}
                                    </option>
                                  );
                                })}
                              </select>
                              {selectedMaterialData && (
                                <div className="mt-2">
                                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                    Calculation Mode
                                  </label>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        console.log('Switching to weight-based mode', { 
                                          currentMode: material.is_weight_based, 
                                          index,
                                          material 
                                        });
                                        updateMaterial(index, {
                                          is_weight_based: true,
                                          weight_used: material.weight_used || 0,
                                          weight_unit: selectedMaterialData.weight_unit || 'g'
                                        });
                                      }}
                                      className={`flex-1 px-3 py-2 text-xs rounded border ${
                                        isWeightBased 
                                          ? 'bg-blue-500 text-white border-blue-500' 
                                          : `${theme.border} ${theme.textSecondary} hover:${theme.bgSecondary}`
                                      }`}
                                    >
                                      ⚖️ By Weight
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        console.log('Switching to quantity-based mode', { 
                                          currentMode: material.is_weight_based, 
                                          index,
                                          material 
                                        });
                                        updateMaterial(index, {
                                          is_weight_based: false,
                                          quantity: material.quantity || 0,
                                          unit: selectedMaterialData.unit_of_measure || 'pieces'
                                        });
                                      }}
                                      className={`flex-1 px-3 py-2 text-xs rounded border ${
                                        !isWeightBased 
                                          ? 'bg-blue-500 text-white border-blue-500' 
                                          : `${theme.border} ${theme.textSecondary} hover:${theme.bgSecondary}`
                                      }`}
                                    >
                                      🔢 By Quantity
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            {isWeightBased ? (
                              // Weight-based inputs
                              <>
                                {/* Material package info */}
                                {selectedMaterialData && !selectedMaterialData.weight && (
                                  <div className="col-span-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3 mb-2">
                                    <p className={`text-xs ${theme.textSecondary} mb-2`}>
                                      ℹ️ Enter the weight of one unit/package of this material:
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                          Weight per unit
                                        </label>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={material.material_package_weight || 0}
                                          onChange={(e) => updateMaterial(index, 'material_package_weight', e.target.value)}
                                          placeholder="e.g., 1000 for 1kg"
                                          className={`w-full px-2 py-1 border ${theme.border} rounded text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                                        />
                                      </div>
                                      <div>
                                        <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                          Unit
                                        </label>
                                        <select
                                          value={material.material_package_unit || 'g'}
                                          onChange={(e) => updateMaterial(index, 'material_package_unit', e.target.value)}
                                          className={`w-full px-2 py-1 border ${theme.border} rounded text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                                        >
                                          <option value="g">g</option>
                                          <option value="kg">kg</option>
                                          <option value="lb">lb</option>
                                          <option value="oz">oz</option>
                                        </select>
                                      </div>
                                    </div>
                                    <p className={`text-xs ${theme.textSecondary} mt-1`}>
                                      Example: If material costs ${(selectedMaterialData.cost_per_unit || 0).toFixed(2)} per 1kg spool, enter 1000g or 1kg
                                    </p>
                                  </div>
                                )}
                                
                                <div>
                                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                    Weight Used
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={material.weight_used || 0}
                                    onChange={(e) => updateMaterial(index, 'weight_used', e.target.value)}
                                    placeholder="Enter weight"
                                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                                  />
                                </div>
                                <div>
                                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                    Unit
                                  </label>
                                  <select
                                    value={material.weight_unit || 'g'}
                                    onChange={(e) => updateMaterial(index, 'weight_unit', e.target.value)}
                                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                                  >
                                    <option value="g">g (grams)</option>
                                    <option value="kg">kg (kilograms)</option>
                                    <option value="lb">lb (pounds)</option>
                                    <option value="oz">oz (ounces)</option>
                                  </select>
                                  {selectedMaterialData && (
                                    <p className={`text-xs ${theme.textSecondary} mt-0.5`}>
                                      Material: ${(selectedMaterialData.cost_per_unit || 0).toFixed(2)}/{selectedMaterialData.unit_of_measure}
                                    </p>
                                  )}
                                </div>
                              </>
                            ) : (
                              // Piece-based inputs
                              <>
                                <div>
                                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                    Quantity (pieces)
                                  </label>
                                  <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={material.quantity || 0}
                                    onChange={(e) => updateMaterial(index, 'quantity', e.target.value)}
                                    placeholder="Enter quantity"
                                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                                  />
                                </div>
                                <div>
                                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                    Cost/piece
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={material.cost_per_unit || 0}
                                    onChange={(e) => updateMaterial(index, 'cost_per_unit', e.target.value)}
                                    placeholder="0.00"
                                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                                    readOnly={selectedMaterialData}
                                  />
                                  <p className={`text-xs ${theme.textSecondary} mt-0.5`}>
                                    {selectedMaterialData ? 'From material' : 'Enter cost'}
                                  </p>
                                </div>
                              </>
                            )}

                            <div className="flex items-end gap-2">
                              <div className="flex-1">
                                <label className={`text-xs ${theme.textSecondary} mb-1 block`}>
                                  Usage & Total
                                </label>
                                <div className={`px-3 py-2 ${theme.bgSecondary} border ${theme.border} rounded-lg text-sm`}>
                                  {(() => {
                                    if (isWeightBased) {
                                      const weightUsed = material.weight_used || 0;
                                      const weightUnit = material.weight_unit || 'g';
                                      const costPerGram = material.calculated_cost_per_gram || 0;
                                      const totalCost = material.total_cost || 0;
                                      
                                      return (
                                        <>
                                          <div className={`font-bold text-blue-600 dark:text-blue-400`}>
                                            {weightUsed}{weightUnit}
                                          </div>
                                          <div className={`text-xs ${theme.textSecondary} mt-0.5`}>
                                            {weightUsed}{weightUnit} × ${costPerGram.toFixed(4)}/g
                                          </div>
                                          <div className={`text-xs ${theme.textPrimary} font-bold mt-1 border-t ${theme.border} pt-1`}>
                                            = ${totalCost.toFixed(2)}
                                          </div>
                                        </>
                                      );
                                    } else {
                                      const quantity = material.quantity || 0;
                                      const costPerUnit = material.cost_per_unit || 0;
                                      const totalCost = material.total_cost || 0;
                                      
                                      return (
                                        <>
                                          <div className={`font-bold text-blue-600 dark:text-blue-400`}>
                                            {quantity} pcs
                                          </div>
                                          <div className={`text-xs ${theme.textSecondary} mt-0.5`}>
                                            {quantity} × ${costPerUnit.toFixed(2)}/pc
                                          </div>
                                          <div className={`text-xs ${theme.textPrimary} font-bold mt-1 border-t ${theme.border} pt-1`}>
                                            = ${totalCost.toFixed(2)}
                                          </div>
                                        </>
                                      );
                                    }
                                  })()}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeMaterial(index)}
                                className="px-2 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                title="Remove material"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : (
              <div className={`p-4 ${theme.bgSecondary} border ${theme.border} rounded-lg text-center`}>
                <p className={`text-sm ${theme.textSecondary}`}>
                  No materials added yet. Click "+ Add Material" to begin.
                </p>
              </div>
            )}
          </div>

          {/* Labor & Overhead */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className={`text-sm font-medium mb-2 block ${theme.textPrimary}`}>
                Labor Cost ($) <span className={`text-xs font-normal ${theme.textSecondary}`}>(Optional)</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.cost_breakdown?.labor_cost || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    cost_breakdown: {
                      ...formData.cost_breakdown,
                      labor_cost: e.target.value ? parseFloat(e.target.value) : 0
                    }
                  });
                  setHasUnsavedChanges(true);
                }}
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                placeholder="Optional - Enter labor cost"
              />
            </div>
            <div>
              <label className={`text-sm font-medium mb-2 block ${theme.textPrimary}`}>
                Overhead Cost ($) <span className={`text-xs font-normal ${theme.textSecondary}`}>(Optional)</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.cost_breakdown?.overhead_cost || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    cost_breakdown: {
                      ...formData.cost_breakdown,
                      overhead_cost: e.target.value ? parseFloat(e.target.value) : 0
                    }
                  });
                  setHasUnsavedChanges(true);
                }}
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                placeholder="Optional - Enter overhead cost"
              />
            </div>
          </div>

          {/* Cost Summary */}
          <div className={`p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border ${theme.border} rounded-lg`}>
            <h5 className={`text-sm font-semibold mb-3 ${theme.textPrimary}`}>Cost Summary</h5>
            <div className="space-y-2">
              {/* Material breakdown with calculation display */}
              {formData.cost_breakdown?.materials && formData.cost_breakdown.materials.length > 0 && (
                <div className="mb-3">
                  <div className="flex justify-between text-sm font-medium mb-2">
                    <span className={theme.textSecondary}>Materials Breakdown:</span>
                  </div>
                  <div className="space-y-2">
                    {formData.cost_breakdown.materials.map((mat, idx) => {
                      const matData = materials?.find(m => m.id === mat.material_id);
                      if (!matData) return null;
                      
                      // Determine usage display and calculation based on material type
                      let usageDisplay = '';
                      let calculationDisplay = '';
                      let usageType = '';
                      
                      if (mat.is_weight_based) {
                        const weightValue = mat.weight_used || 0;
                        const weightUnit = mat.weight_unit || 'g';
                        const costPerGram = mat.calculated_cost_per_gram || 0;
                        usageDisplay = `${weightValue}${weightUnit}`;
                        calculationDisplay = `${weightValue}${weightUnit} × $${costPerGram.toFixed(4)}/g`;
                        usageType = '⚖️ Weight-based';
                      } else {
                        const quantityValue = mat.quantity || 0;
                        const costPerUnit = mat.cost_per_unit || 0;
                        usageDisplay = `${quantityValue} pcs`;
                        calculationDisplay = `${quantityValue} × $${costPerUnit.toFixed(2)}/pc`;
                        usageType = '🔢 Piece-based';
                      }
                      
                      return (
                        <div key={idx} className={`${theme.card} p-2 rounded border ${theme.border}`}>
                          <div className={`flex justify-between items-start text-xs ${theme.textPrimary}`}>
                            <div className="flex-1">
                              <div className="font-medium">{matData.name}</div>
                              <div className={`text-xs ${theme.textSecondary} mt-0.5`}>{usageType}</div>
                              <div className={`text-xs ${theme.textSecondary} mt-1 font-mono`}>{calculationDisplay}</div>
                            </div>
                            <div className="text-right ml-2">
                              <div className="font-bold text-blue-600 dark:text-blue-400">{usageDisplay}</div>
                              <div className={`text-sm ${theme.textPrimary} font-bold mt-1`}>${(mat.total_cost || 0).toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between text-sm">
                <span className={theme.textSecondary}>Total Material Cost:</span>
                <span className={`font-medium ${theme.textPrimary}`}>${materialCost.toFixed(2)}</span>
              </div>
              
              {laborCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className={theme.textSecondary}>Labor Cost:</span>
                  <span className={`font-medium ${theme.textPrimary}`}>${laborCost.toFixed(2)}</span>
                </div>
              )}
              
              {overheadCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className={theme.textSecondary}>Overhead Cost:</span>
                  <span className={`font-medium ${theme.textPrimary}`}>${overheadCost.toFixed(2)}</span>
                </div>
              )}
              
              <div className={`flex justify-between text-sm pt-2 border-t ${theme.border}`}>
                <span className={`font-semibold ${theme.textPrimary}`}>Total Cost:</span>
                <span className={`font-bold ${theme.textPrimary}`}>${totalCost.toFixed(2)}</span>
              </div>
              
              {productPrice > 0 && (
                <>
                  <div className={`flex justify-between text-sm pt-2 border-t ${theme.border}`}>
                    <span className={`font-semibold ${theme.textPrimary}`}>Product Price:</span>
                    <span className={`font-bold ${theme.textPrimary}`}>${productPrice.toFixed(2)}</span>
                  </div>
                  
                  <div className={`flex justify-between text-sm ${profitAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    <span className="font-medium">Profit Amount:</span>
                    <span className="font-bold">
                      {profitAmount >= 0 ? '+' : ''}${profitAmount.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                    <span className={`font-bold text-lg ${theme.textPrimary}`}>True Profit Margin:</span>
                    <span className={`font-bold text-xl ${profitMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {profitMargin.toFixed(2)}%
                    </span>
                  </div>
                  
                  {profitMargin < 0 && (
                    <div className={`mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded text-xs ${theme.textPrimary}`}>
                      ⚠️ Warning: Product price is below total cost. You're losing ${Math.abs(profitAmount).toFixed(2)} per unit.
                    </div>
                  )}
                  
                  {profitMargin >= 0 && profitMargin < 10 && (
                    <div className={`mt-2 p-2 bg-amber-100 dark:bg-amber-900/30 rounded text-xs ${theme.textPrimary}`}>
                      💡 Low profit margin: Consider adjusting price or reducing costs for better profitability.
                    </div>
                  )}
                </>
              )}
              
              {!productPrice && totalCost > 0 && (
                <div className={`mt-2 p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-xs ${theme.textPrimary}`}>
                  ℹ️ Enter a product price above to see profit margin calculation.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomization = () => (
    <div className="space-y-4">
      {/* Product Options */}
      <div className={`${theme.bgSecondary} p-5 rounded-lg border ${theme.border}`}>
        <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Product Options (Size, Color, etc.)</h4>
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              const optionName = prompt('Enter option name (e.g. "Size", "Color"):');
              if (optionName) {
                setFormData({
                  ...formData,
                  base_options: {
                    ...formData.base_options,
                    [optionName]: []
                  }
                });
                setHasUnsavedChanges(true);
              }
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600 transition-colors"
          >
            + Add Option Category
          </button>
          
          {Object.entries(formData.base_options || {}).map(([optionName, options]) => (
            <div key={optionName} className={`border ${theme.border} p-4 rounded-lg ${theme.card}`}>
              <div className="flex justify-between items-center mb-3">
                <h5 className={`font-medium ${theme.textPrimary}`}>{optionName}</h5>
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = {...formData.base_options};
                    delete newOptions[optionName];
                    setFormData({...formData, base_options: newOptions});
                    setHasUnsavedChanges(true);
                  }}
                  className="text-red-500 text-sm hover:text-red-700"
                >
                  Remove Category
                </button>
              </div>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={option.name}
                      onChange={(e) => {
                        const newOptions = [...options];
                        newOptions[index] = {...option, name: e.target.value};
                        setFormData({
                          ...formData,
                          base_options: {
                            ...formData.base_options,
                            [optionName]: newOptions
                          }
                        });
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Option name"
                      className={`flex-1 px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={option.price}
                      onChange={(e) => {
                        const newOptions = [...options];
                        newOptions[index] = {...option, price: parseFloat(e.target.value) || 0};
                        setFormData({
                          ...formData,
                          base_options: {
                            ...formData.base_options,
                            [optionName]: newOptions
                          }
                        });
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Extra $"
                      className={`w-24 px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = options.filter((_, i) => i !== index);
                        setFormData({
                          ...formData,
                          base_options: {
                            ...formData.base_options,
                            [optionName]: newOptions
                          }
                        });
                        setHasUnsavedChanges(true);
                      }}
                      className="text-red-500 text-sm hover:text-red-700 px-2"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      base_options: {
                        ...formData.base_options,
                        [optionName]: [...options, {name: '', price: 0}]
                      }
                    });
                    setHasUnsavedChanges(true);
                  }}
                  className="text-blue-500 text-sm hover:text-blue-700"
                >
                  + Add Option
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Text Inputs */}
      <div className={`${theme.bgSecondary} p-5 rounded-lg border ${theme.border}`}>
        <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Custom Text Inputs (Engraving, Instructions, etc.)</h4>
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              const fieldName = prompt('Enter field name (e.g. "Engraving Text", "Special Instructions"):');
              if (fieldName) {
                setFormData({
                  ...formData,
                  custom_options: {
                    ...formData.custom_options,
                    [fieldName]: {
                      input_type: 'text',
                      character_limit: null,
                      placeholder: '',
                      required: false,
                      price: 0.0
                    }
                  }
                });
                setHasUnsavedChanges(true);
              }
            }}
            className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-600 transition-colors"
          >
            + Add Text Input
          </button>
          
          {Object.entries(formData.custom_options || {}).map(([fieldName, config]) => (
            <div key={fieldName} className={`border ${theme.border} p-4 rounded-lg ${theme.card}`}>
              <div className="flex justify-between items-center mb-3">
                <h5 className={`font-medium ${theme.textPrimary}`}>{fieldName}</h5>
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = {...formData.custom_options};
                    delete newOptions[fieldName];
                    setFormData({...formData, custom_options: newOptions});
                    setHasUnsavedChanges(true);
                  }}
                  className="text-red-500 text-sm hover:text-red-700"
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Input Type</label>
                  <select
                    value={config.input_type}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        custom_options: {
                          ...formData.custom_options,
                          [fieldName]: {...config, input_type: e.target.value}
                        }
                      });
                      setHasUnsavedChanges(true);
                    }}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                  >
                    <option value="text">Single Line</option>
                    <option value="textarea">Multi Line</option>
                  </select>
                </div>
                <div>
                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Character Limit</label>
                  <input
                    type="number"
                    value={config.character_limit || ''}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        custom_options: {
                          ...formData.custom_options,
                          [fieldName]: {...config, character_limit: parseInt(e.target.value) || null}
                        }
                      });
                      setHasUnsavedChanges(true);
                    }}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                    placeholder="Max chars"
                  />
                </div>
                <div>
                  <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Additional Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={config.price || 0}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        custom_options: {
                          ...formData.custom_options,
                          [fieldName]: {...config, price: parseFloat(e.target.value) || 0}
                        }
                      });
                      setHasUnsavedChanges(true);
                    }}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                    placeholder="0.00"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.required || false}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          custom_options: {
                            ...formData.custom_options,
                            [fieldName]: {...config, required: e.target.checked}
                          }
                        });
                        setHasUnsavedChanges(true);
                      }}
                      className="mr-2"
                    />
                    <span className={`text-xs ${theme.textSecondary}`}>Required Field</span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add-ons */}
      <div className={`${theme.bgSecondary} p-5 rounded-lg border ${theme.border}`}>
        <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>Add-ons (Optional products customers can add)</h4>
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              const newAddon = {
                id: Date.now().toString(),
                addon_type: 'custom',
                name: '',
                description: '',
                price: 0,
                category: '',
                available_separately: true,
                preview_images: [],
                linked_product_id: null
              };
              setFormData({
                ...formData,
                addons: [...(formData.addons || []), newAddon]
              });
              setHasUnsavedChanges(true);
            }}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-600 transition-colors"
          >
            + Add Add-on
          </button>
          
          {(formData.addons || []).map((addon, index) => (
            <div key={addon.id} className={`border ${theme.border} p-4 rounded-lg ${theme.card}`}>
              <div className="flex justify-between items-center mb-3">
                <h5 className={`font-medium ${theme.textPrimary}`}>{addon.name || 'New Add-on'}</h5>
                <button
                  type="button"
                  onClick={() => {
                    const newAddons = formData.addons.filter((_, i) => i !== index);
                    setFormData({...formData, addons: newAddons});
                    setHasUnsavedChanges(true);
                  }}
                  className="text-red-500 text-sm hover:text-red-700"
                >
                  Remove
                </button>
              </div>
              
              {/* Add-on Type Selector */}
              <div className="mb-3">
                <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Add-on Type</label>
                <select
                  value={addon.addon_type || 'custom'}
                  onChange={(e) => {
                    const newAddons = [...formData.addons];
                    const addonType = e.target.value;
                    newAddons[index] = {
                      ...addon, 
                      addon_type: addonType,
                      // Reset fields when switching types
                      linked_product_id: addonType === 'custom' ? null : addon.linked_product_id,
                      preview_images: addonType === 'existing_product' ? [] : addon.preview_images
                    };
                    setFormData({...formData, addons: newAddons});
                    setHasUnsavedChanges(true);
                  }}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                >
                  <option value="custom">Custom Add-on</option>
                  <option value="existing_product">Existing Product</option>
                </select>
              </div>

              {addon.addon_type === 'existing_product' ? (
                /* Existing Product Add-on */
                <div className="space-y-3">
                  <div>
                    <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Select Product *</label>
                    <select
                      value={addon.linked_product_id || ''}
                      onChange={(e) => {
                        const newAddons = [...formData.addons];
                        const selectedProductId = e.target.value;
                        const selectedProduct = products.find(p => p.id === selectedProductId);
                        
                        if (selectedProduct) {
                          // Auto-populate from selected product
                          newAddons[index] = {
                            ...addon,
                            linked_product_id: selectedProductId,
                            name: selectedProduct.name,
                            description: selectedProduct.description || '',
                            price: selectedProduct.price,
                            category: selectedProduct.category || ''
                          };
                        } else {
                          newAddons[index] = {
                            ...addon,
                            linked_product_id: null,
                            name: '',
                            price: 0
                          };
                        }
                        
                        setFormData({...formData, addons: newAddons});
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                      required
                    >
                      <option value="">-- Select a product --</option>
                      {products
                        .filter(p => p.id !== formData.id && p.in_stock) // Exclude current product and out-of-stock items
                        .map(product => {
                          const thumbnail = product.images?.[0] || product.image_url || '';
                          return (
                            <option key={product.id} value={product.id}>
                              {product.name} - ${product.price.toFixed(2)} {product.stock_quantity > 0 ? `(${product.stock_quantity} in stock)` : ''}
                            </option>
                          );
                        })}
                    </select>
                    <p className={`text-xs ${theme.textSecondary} mt-1`}>
                      💡 Selecting a product auto-fills name, price, and shows first image
                    </p>
                  </div>
                  
                  {/* Product Preview */}
                  {addon.linked_product_id && (() => {
                    const linkedProduct = products.find(p => p.id === addon.linked_product_id);
                    const thumbnail = linkedProduct?.images?.[0] || linkedProduct?.image_url;
                    return linkedProduct && (
                      <div className={`p-3 rounded-lg border ${theme.border} bg-blue-50`}>
                        <p className={`text-xs font-medium ${theme.textSecondary} mb-2`}>Product Preview:</p>
                        <div className="flex items-start gap-3">
                          {thumbnail && (
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}${thumbnail}`}
                              alt={linkedProduct.name}
                              className="w-16 h-16 object-cover rounded border"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div className="flex-1">
                            <p className={`font-medium text-sm ${theme.textPrimary}`}>{linkedProduct.name}</p>
                            <p className={`text-xs ${theme.textSecondary}`}>Price: ${linkedProduct.price.toFixed(2)}</p>
                            <p className={`text-xs ${theme.textSecondary}`}>Stock: {linkedProduct.stock_quantity || 0} units</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Custom Add-on */
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Add-on Name *</label>
                      <input
                        type="text"
                        value={addon.name}
                        onChange={(e) => {
                          const newAddons = [...formData.addons];
                          newAddons[index] = {...addon, name: e.target.value};
                          setFormData({...formData, addons: newAddons});
                          setHasUnsavedChanges(true);
                        }}
                        className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                        placeholder="e.g., Gift Wrapping"
                        required
                      />
                    </div>
                    <div>
                      <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Price ($) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={addon.price}
                        onChange={(e) => {
                          const newAddons = [...formData.addons];
                          newAddons[index] = {...addon, price: parseFloat(e.target.value) || 0};
                          setFormData({...formData, addons: newAddons});
                          setHasUnsavedChanges(true);
                        }}
                        className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Description</label>
                    <textarea
                      value={addon.description}
                      onChange={(e) => {
                        const newAddons = [...formData.addons];
                        newAddons[index] = {...addon, description: e.target.value};
                        setFormData({...formData, addons: newAddons});
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                      placeholder="Brief description of this add-on"
                      rows="2"
                    />
                  </div>
                  
                  {/* Custom Add-on Image Upload */}
                  <div>
                    <label className={`text-xs ${theme.textSecondary} mb-1 block`}>Thumbnail Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const uploadFormData = new FormData();
                            uploadFormData.append('file', file);
                            
                            const response = await handleAddonImageUpload(uploadFormData);
                            if (response?.file_url) {
                              setFormData(prevFormData => {
                                const newAddons = [...prevFormData.addons];
                                newAddons[index] = {
                                  ...addon,
                                  preview_images: [response.file_url]
                                };
                                return {...prevFormData, addons: newAddons};
                              });
                              setHasUnsavedChanges(true);
                            }
                          } catch (error) {
                            console.error('Image upload failed:', error);
                          }
                        }
                      }}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.bgPrimary} ${theme.textPrimary}`}
                      disabled={uploadingAddonImages}
                    />
                    {addon.preview_images && addon.preview_images.length > 0 && (
                      <div className="mt-2">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${addon.preview_images[0]}`}
                          alt={addon.name}
                          className="w-20 h-20 object-cover rounded border"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <p className={`text-xs ${theme.textSecondary} mt-1`}>
                      📷 Upload a thumbnail for this custom add-on
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCustomerFiles = () => (
    <div className="space-y-4">
      <div className={`${theme.bgSecondary} p-5 rounded-lg border ${theme.border}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <span className="text-indigo-600 dark:text-indigo-400 font-bold">📎</span>
          </div>
          <h4 className={`text-base font-semibold ${theme.textPrimary}`}>Customer File Upload Settings</h4>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="file_upload_enabled"
              checked={formData.file_upload_enabled}
              onChange={(e) => {
                setFormData({
                  ...formData, 
                  file_upload_enabled: e.target.checked,
                  file_upload_required: e.target.checked ? formData.file_upload_required : false
                });
                setHasUnsavedChanges(true);
              }}
              className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="file_upload_enabled" className="ml-3">
              <div className={`text-sm font-medium ${theme.textPrimary}`}>Enable File Uploads</div>
              <div className={`text-xs ${theme.textSecondary}`}>Allow customers to upload images/files when purchasing this product</div>
            </label>
          </div>

          {formData.file_upload_enabled && (
            <div className="ml-7 pl-4 border-l-2 border-indigo-200 dark:border-indigo-800 space-y-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="file_upload_required"
                  checked={formData.file_upload_required}
                  onChange={(e) => {
                    setFormData({...formData, file_upload_required: e.target.checked});
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="file_upload_required" className="ml-3">
                  <div className={`text-sm font-medium ${theme.textPrimary}`}>Make Upload Mandatory</div>
                  <div className={`text-xs ${theme.textSecondary}`}>Customers must upload files to complete purchase</div>
                </label>
              </div>

              {/* Upload Restrictions */}
              <div className={`${theme.card} p-4 rounded-lg border ${theme.border}`}>
                <h5 className={`text-sm font-semibold mb-3 ${theme.textPrimary}`}>Upload Restrictions</h5>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme.textPrimary}`}>Max Files Allowed</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={formData.max_files_allowed}
                      onChange={(e) => {
                        setFormData({...formData, max_files_allowed: parseInt(e.target.value) || 1});
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-full px-3 py-2 text-sm border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                    />
                    <p className={`text-xs mt-1 ${theme.textSecondary}`}>1-50 files</p>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme.textPrimary}`}>Max File Size (MB)</label>
                    <input
                      type="number"
                      min="0.1"
                      max="100"
                      step="0.1"
                      value={formData.max_file_size_mb}
                      onChange={(e) => {
                        setFormData({...formData, max_file_size_mb: parseFloat(e.target.value) || 1});
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-full px-3 py-2 text-sm border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                    />
                    <p className={`text-xs mt-1 ${theme.textSecondary}`}>Per file (0.1-100 MB)</p>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme.textPrimary}`}>
                      Allowed File Types
                    </label>
                    <input
                      type="text"
                      value={formData.allowed_file_types}
                      onChange={(e) => {
                        setFormData({...formData, allowed_file_types: e.target.value});
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-full px-3 py-2 text-sm border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                      placeholder="image/*,.pdf"
                    />
                    <p className={`text-xs mt-1 ${theme.textSecondary}`}>Comma-separated</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderShipping = () => (
    <div className="space-y-4">
      <div className={`${theme.bgSecondary} p-5 rounded-lg border ${theme.border}`}>
        <h4 className={`font-semibold mb-3 ${theme.textPrimary}`}>📦 Shipping Information</h4>
        <p className={`text-sm ${theme.textSecondary} mb-4`}>This information is used for accurate shipping quotes</p>
        
        {/* Weight */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Weight</label>
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.weight}
                onChange={(e) => {
                  setFormData({...formData, weight: parseFloat(e.target.value) || 0.0});
                  setHasUnsavedChanges(true);
                }}
                className={`flex-1 px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                placeholder="0.00"
              />
              <select
                value={formData.weight_unit}
                onChange={(e) => {
                  setFormData({...formData, weight_unit: e.target.value});
                  setHasUnsavedChanges(true);
                }}
                className={`px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
              >
                <option value="lb">lbs</option>
                <option value="kg">kg</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Package Type</label>
            <select
              value={formData.package_type}
              onChange={(e) => {
                setFormData({...formData, package_type: e.target.value});
                setHasUnsavedChanges(true);
              }}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
            >
              <option value="package">Package</option>
              <option value="envelope">Envelope</option>
              <option value="box">Box</option>
              <option value="tube">Tube</option>
            </select>
          </div>
        </div>

        {/* Dimensions */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Dimensions (L × W × H)</label>
          <div className="grid grid-cols-4 gap-2">
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.length}
              onChange={(e) => {
                setFormData({...formData, length: parseFloat(e.target.value) || 0.0});
                setHasUnsavedChanges(true);
              }}
              className={`px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
              placeholder="Length"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.width}
              onChange={(e) => {
                setFormData({...formData, width: parseFloat(e.target.value) || 0.0});
                setHasUnsavedChanges(true);
              }}
              className={`px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
              placeholder="Width"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.height}
              onChange={(e) => {
                setFormData({...formData, height: parseFloat(e.target.value) || 0.0});
                setHasUnsavedChanges(true);
              }}
              className={`px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
              placeholder="Height"
            />
            <select
              value={formData.dimension_unit}
              onChange={(e) => {
                setFormData({...formData, dimension_unit: e.target.value});
                setHasUnsavedChanges(true);
              }}
              className={`px-3 py-2 border ${theme.border} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
            >
              <option value="inch">inches</option>
              <option value="cm">cm</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
      {/* Header with Mode Toggle */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className={`text-xl font-bold ${theme.textPrimary}`}>
            {editingProduct ? '✏️ Edit Product' : '➕ Add New Product'}
          </h3>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>
            {isFullPageMode ? 'All sections visible - scroll to navigate' : 
             isWizardMode ? 'Step-by-step guided creation' : 'Jump to any section to edit'}
          </p>
        </div>
        
        {!isFullPageMode && (
          <button
            type="button"
            onClick={toggleWizardMode}
            className={`flex items-center gap-2 px-4 py-2 ${theme.bgSecondary} border ${theme.border} rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors`}
          >
            <span className="text-lg">{isWizardMode ? '📑' : '🧭'}</span>
            <span className={`text-sm font-medium ${theme.textPrimary}`}>
              {isWizardMode ? 'Switch to Tabs' : 'Switch to Wizard'}
            </span>
          </button>
        )}
      </div>

      <form onSubmit={(e) => {
        handleSubmit(e);
        setHasUnsavedChanges(false);
      }} className="space-y-6">
        {/* Progress Bar (Wizard Mode Only) */}
        {!isFullPageMode && renderProgressBar()}

        {/* Tab Navigation (Not in Full Page Mode) */}
        {!isFullPageMode && renderTabNavigation()}

        {/* Tab Content or Full Page Content */}
        {isFullPageMode ? (
          <div className="space-y-8">
            {/* Section Headers for Full Page */}
            <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
              <h4 className={`text-lg font-bold ${theme.textPrimary} mb-1`}>📝 Basic Information</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Essential product details</p>
            </div>
            {renderBasicInfo()}

            <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
              <h4 className={`text-lg font-bold ${theme.textPrimary} mb-1`}>🖼️ Images & Media</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Product photos and gallery</p>
            </div>
            {renderImages()}

            <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
              <h4 className={`text-lg font-bold ${theme.textPrimary} mb-1`}>📦 Inventory & Stock</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Stock tracking and availability</p>
            </div>
            {renderInventory()}

            <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
              <h4 className={`text-lg font-bold ${theme.textPrimary} mb-1`}>💰 Costing Model</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Materials and cost breakdown</p>
            </div>
            {renderCostingModel()}

            <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
              <h4 className={`text-lg font-bold ${theme.textPrimary} mb-1`}>⚙️ Customization Options</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Product variants and custom inputs</p>
            </div>
            {renderCustomization()}

            <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
              <h4 className={`text-lg font-bold ${theme.textPrimary} mb-1`}>📎 Customer File Uploads</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Upload settings for customers</p>
            </div>
            {renderCustomerFiles()}

            <div className={`p-4 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
              <h4 className={`text-lg font-bold ${theme.textPrimary} mb-1`}>🚚 Shipping Details</h4>
              <p className={`text-xs ${theme.textSecondary}`}>Dimensions and shipping information</p>
            </div>
            {renderShipping()}
          </div>
        ) : (
          <div className="min-h-[400px]">
            {currentStep === 0 && renderBasicInfo()}
            {currentStep === 1 && renderImages()}
            {currentStep === 2 && renderInventory()}
            {currentStep === 3 && renderCostingModel()}
            {currentStep === 4 && renderCustomization()}
            {currentStep === 5 && renderCustomerFiles()}
            {currentStep === 6 && renderShipping()}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className={`flex justify-between items-center pt-6 border-t ${theme.border}`}>
          <div>
            {!isFullPageMode && isWizardMode && currentStep > 0 && (
              <button
                type="button"
                onClick={handlePrevious}
                className={`px-6 py-2 ${theme.bgSecondary} border ${theme.border} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${theme.textPrimary}`}
              >
                ← Previous
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {!isFullPageMode && isWizardMode && currentStep < tabs.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Next →
              </button>
            ) : (
              <button
                type="submit"
                id="product-form-submit"
                className="px-8 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors font-medium"
              >
                {editingProduct ? '💾 Save Changes' : '✨ Create Product'}
              </button>
            )}
          </div>
        </div>

        {/* Keyboard Shortcut Hint */}
        <div className={`text-xs text-center ${theme.textSecondary}`}>
          💡 Tip: Press <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded">Ctrl+S</kbd> to save quickly
        </div>
      </form>
    </div>
  );
};

export default EnhancedProductForm;